/* Header content menus: theme disclosure handles opening; this element owns tabs only. */
if (!customElements.get('navigation-content-menu')) {
  customElements.define('navigation-content-menu', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-content-tab]')];
      this.panels = [...this.querySelectorAll('[data-content-panel]')];
      this.details = this.querySelector('details');
      if (!this.details) return;

      this.addEventListener('pointerover', (event) => {
        const tab = event.target.closest('[data-content-tab]');
        if (tab && event.pointerType === 'mouse' && matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.selectTab(tab);
        }
      }, { signal });
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-content-tab]');
        if (tab) this.selectTab(tab);
      }, { signal });
      // Keep keyboard activation toggling the hover disclosure.
      this.details.querySelector('summary').addEventListener('click', (event) => {
        if (event.detail === 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.details.open = !this.details.open;
        }
      }, { capture: true, signal });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-content-tab]');
        const summary = event.target.closest('summary');
        if (summary && event.key === 'ArrowDown') {
          event.preventDefault();
          this.details.open = true;
          (this.tabs.find((item) => item.getAttribute('aria-selected') === 'true') || this.tabs[0])?.focus();
        } else if (tab && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const index = this.tabs.indexOf(tab);
          let next = event.key === 'ArrowDown' ? (index + 1) % this.tabs.length : (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          this.selectTab(this.tabs[next]);
          this.tabs[next].focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.details.open = false;
          this.details.querySelector('summary').focus();
        }
      }, { signal });
      this.details.addEventListener('toggle', () => {
        this.details.querySelector('summary').setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) this.loadSelectedImages();
      }, { signal });
      this.addEventListener('shopify:block:select', () => {
        this.details.open = true;
        this.loadSelectedImages();
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => { this.details.open = false; }, { signal });
      this.details.querySelector('summary').setAttribute('aria-expanded', String(this.details.open));
    }

    selectTab(tab) {
      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      this.loadSelectedImages();
    }

    loadSelectedImages() {
      if (!this.details.open) return;
      this.querySelectorAll('[data-content-panel]:not([hidden]) img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });

  // Let NavigationDrawer own panel transitions and back navigation. Only reset
  // these new menus' categories and scroll position when their entry is opened.
  function resetMobileContent(panel) {
    panel.querySelectorAll('.navigation-content-mobile__category').forEach((category, index) => {
      category.open = index === 0;
    });
    panel.scrollTop = 0;
  }

  // Native details toggles also cover keyboard activation. Scope the accordion
  // to this menu's panel so other navigation menus keep their own state.
  document.addEventListener('toggle', (event) => {
    const category = event.target;
    if (!category.matches?.('.navigation-content-mobile__category') || !category.open) return;
    category.closest('[data-content-mobile-panel]')?.querySelectorAll('.navigation-content-mobile__category').forEach((sibling) => {
      if (sibling !== category) sibling.open = false;
    });
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-content-mobile-open]');
    if (button) {
      const drawer = button.closest('navigation-drawer');
      const panel = [...(drawer?.querySelectorAll('[data-content-mobile-panel]') || [])].find((item) => item.dataset.contentMobilePanel === button.dataset.contentMobileOpen);
      if (panel) {
        resetMobileContent(panel);
        button.setAttribute('aria-expanded', 'true');
      }
    }
    const back = event.target.closest?.('.navigation-content-mobile__back');
    if (back) {
      const panel = back.closest('[data-content-mobile-panel]');
      const entry = [...back.closest('navigation-drawer').querySelectorAll('[data-content-mobile-open]')].find((item) => item.dataset.contentMobileOpen === panel.dataset.contentMobilePanel);
      entry?.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('dialog:after-hide', (event) => {
    event.target.querySelectorAll?.('[data-content-mobile-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  });

  document.addEventListener('shopify:block:select', (event) => {
    const mobileBlock = event.target.closest?.('.navigation-content-mobile');
    mobileBlock?.querySelector('[data-content-mobile-open]')?.click();
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const mobileBlock = event.target.closest?.('.navigation-content-mobile');
    const button = mobileBlock?.querySelector('[data-content-mobile-open]');
    if (button?.getAttribute('aria-expanded') === 'true') {
      const panel = [...button.closest('navigation-drawer').querySelectorAll('[data-content-mobile-panel]')].find((item) => item.dataset.contentMobilePanel === button.dataset.contentMobileOpen);
      panel?.querySelector('.navigation-content-mobile__back')?.click();
    }
  });
}
